import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import SEOHelmet from "@/components/SEOHelmet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Shield, Lock, Eye, UserCheck, FileText, Building2, Mail, Phone, Cookie } from "lucide-react";

const Datenschutz = () => {
  return (
    <>
      <SEOHelmet />
      <Navigation />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Datenschutz" }
      ]} />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="min-h-screen flex flex-col relative z-10">
      
      <main className="flex-1">
        {/* Hero Section */}
        <ResponsiveSection variant="accent" spacing="lg">
          <div className="text-center">
            <Badge className="mb-4" variant="outline">
              <Shield className="w-3 h-3 mr-1" />
              DSGVO-konform
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 break-words">
              Datenschutzerklärung
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Informationen zur Verarbeitung Ihrer personenbezogenen Daten
            </p>
          </div>
        </ResponsiveSection>

        {/* Thomas Dewein Datenschutzerklärung */}
        <ResponsiveSection spacing="lg" containerSize="lg">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-lg">
              <UserCheck className="w-5 h-5 text-accent" />
              <h2 className="text-2xl sm:text-3xl font-bold">Datenschutzerklärung</h2>
            </div>
            <p className="text-muted-foreground mt-3">Informationen zur Verarbeitung Ihrer personenbezogenen Daten</p>
          </div>

          {/* 1. Verantwortlicher */}
          <Card className="mb-6 border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-accent" />
                1. Verantwortlicher
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="font-semibold text-foreground">Thomas Dewein</p>
              <p className="text-muted-foreground">Riedgartenweg 15<br/>89275 Elchingen</p>
              <div className="space-y-2 pt-2">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-accent" />
                  <a href="tel:073168231" className="text-accent hover:underline">0731 / 68231</a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-accent" />
                  <a href="tel:01636389945" className="text-accent hover:underline">0163 / 6389945</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-accent" />
                  <a href="mailto:info@praxis-versichert.de" className="text-accent hover:underline break-all">info@praxis-versichert.de</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-accent" />
                  <a href="mailto:thomas.dewein@deutsches-maklerforum.de" className="text-accent hover:underline break-all">thomas.dewein@deutsches-maklerforum.de</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Accordion für detaillierte Informationen */}
          <Accordion type="single" collapsible className="w-full space-y-4">
            {/* 2. Arten der verarbeiteten Daten */}
            <AccordionItem value="item-1" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">2. Arten der verarbeiteten Daten</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Kontaktdaten:</strong> Name, Adresse, Telefon, E-Mail</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Versicherungsdaten:</strong> Verträge, Policen, Schadensfälle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Gesundheitsdaten:</strong> Nur für Krankenversicherungen (besondere Kategorie nach Art. 9 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Finanzielle Daten:</strong> Einkommen, Vermögen bei Finanzberatung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Kommunikationsdaten:</strong> E-Mails, Telefonate, Beratungsprotokolle</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 3. Zwecke der Datenverarbeitung */}
            <AccordionItem value="item-2" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">3. Zwecke der Datenverarbeitung</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Beratung und Vermittlung von Versicherungen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Vertragsanbahnung und -abwicklung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Kundenbetreuung und Follow-up</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Erfüllung gesetzlicher Pflichten (§ 34d GewO, VVG)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Dokumentationspflichten</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 4. Rechtsgrundlagen */}
            <AccordionItem value="item-3" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">4. Rechtsgrundlagen (Art. 6 DSGVO)</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Art. 6 Abs. 1 lit. a DSGVO:</strong> Einwilligung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Art. 6 Abs. 1 lit. b DSGVO:</strong> Vertragserfüllung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Art. 6 Abs. 1 lit. c DSGVO:</strong> Rechtliche Verpflichtung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Art. 6 Abs. 1 lit. f DSGVO:</strong> Berechtigte Interessen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Art. 9 Abs. 2 lit. a DSGVO:</strong> Einwilligung für Gesundheitsdaten</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 5. Empfänger der Daten */}
            <AccordionItem value="item-4" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">5. Empfänger der Daten</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Versicherungsgesellschaften (zur Angebotserstellung und Vertragsabschluss)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>IT-Dienstleister (Hosting, CRM-Systeme)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Steuerberater, Rechtsanwälte (bei Bedarf)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Aufsichtsbehörden (bei gesetzlicher Verpflichtung)</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 6. Speicherdauer */}
            <AccordionItem value="item-5" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">6. Speicherdauer</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Während der Geschäftsbeziehung</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Nach Vertragsende: 10 Jahre (Aufbewahrungspflichten nach HGB, AO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Gesundheitsdaten: Löschung nach Ende der Aufbewahrungsfrist</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 7. Ihre Rechte */}
            <AccordionItem value="item-6" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <UserCheck className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">7. Ihre Rechte</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <p className="mb-2">Sie haben folgende Rechte nach der DSGVO:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Auskunftsrecht</strong> (Art. 15 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Berichtigungsrecht</strong> (Art. 16 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Löschungsrecht</strong> (Art. 17 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Datenübertragbarkeit</strong> (Art. 20 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Widerspruchsrecht</strong> (Art. 21 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Widerruf der Einwilligung</strong> (Art. 7 Abs. 3 DSGVO)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span><strong className="text-foreground">Beschwerderecht</strong> bei Aufsichtsbehörde (Art. 77 DSGVO)</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 8. Aufsichtsbehörde */}
            <AccordionItem value="item-7" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">8. Aufsichtsbehörde</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-2">
                <p className="font-semibold text-foreground">Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)</p>
                <p>Promenade 18<br/>91522 Ansbach</p>
                <p className="pt-2">Telefon: 0981 180093-0</p>
                <p>E-Mail: poststelle@lda.bayern.de</p>
                <p>Website: <a href="https://www.lda.bayern.de" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.lda.bayern.de</a></p>
              </AccordionContent>
            </AccordionItem>

            {/* 9. Datensicherheit */}
            <AccordionItem value="item-8" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">9. Datensicherheit</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4 space-y-3">
                <p>Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>SSL-Verschlüsselung der Website</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Zugriffskontrollen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Regelmäßige Sicherheitsupdates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-accent mt-1">•</span>
                    <span>Verschlüsselte E-Mail-Kommunikation (auf Anfrage)</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 10. Keine automatisierte Entscheidungsfindung */}
            <AccordionItem value="item-9" className="border border-accent/20 rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-left">10. Keine automatisierte Entscheidungsfindung</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pt-4">
                <p>Es finden keine automatisierten Entscheidungen (Profiling) im Sinne von Art. 22 DSGVO statt. Alle Beratungen und Entscheidungen erfolgen persönlich durch unsere Mitarbeiter.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ResponsiveSection>


        {/* Website-spezifische Informationen */}
        <ResponsiveSection variant="card" spacing="lg" containerSize="lg">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Website-spezifische Informationen</h2>
            <p className="text-muted-foreground">Zusätzliche Datenschutzinformationen für praxis-versichert.de</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card className="border-accent/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-accent" />
                  Cookies
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Diese Website verwendet Cookies für notwendige Funktionen sowie optional für Analytics und Marketing.</p>
                <p>Sie können Ihre Cookie-Einstellungen jederzeit über den Cookie-Banner verwalten.</p>
              </CardContent>
            </Card>

            <Card className="border-accent/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-accent" />
                  Kontaktformulare
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Daten aus Kontaktanfragen werden zur Bearbeitung Ihrer Anfrage gespeichert.</p>
                <p>Speicherdauer: Bis zur vollständigen Bearbeitung Ihrer Anfrage plus gesetzliche Aufbewahrungsfristen.</p>
              </CardContent>
            </Card>

            <Card className="border-accent/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lock className="w-5 h-5 text-accent" />
                  SSL-Verschlüsselung
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Diese Website nutzt SSL-Verschlüsselung (HTTPS) für sichere Datenübertragung.</p>
                <p>Verschlüsselte Seiten erkennen Sie am Schloss-Symbol in der Adressleiste Ihres Browsers.</p>
              </CardContent>
            </Card>

            <Card className="border-accent/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent" />
                  Server-Logs
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>Der Provider dieser Website erhebt automatisch Informationen in Server-Log-Dateien (IP-Adresse, Browser, Datum/Uhrzeit).</p>
                <p>Speicherdauer: 7 Tage. Zweck: Sicherheit und Fehleranalyse.</p>
              </CardContent>
            </Card>
          </div>
        </ResponsiveSection>

        {/* Kontakt bei Datenschutzfragen */}
        <ResponsiveSection spacing="lg" containerSize="md">
          <Card className="border-accent/20 bg-accent/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-6 h-6 text-accent" />
                Kontakt bei Datenschutzfragen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-sm">
              <div>
                <p className="font-semibold text-foreground mb-2">An Thomas Dewein:</p>
                <p className="text-muted-foreground">Thomas Dewein<br/>Riedgartenweg 15<br/>89275 Elchingen</p>
                <p className="text-muted-foreground pt-2">
                  E-Mail: <a href="mailto:info@praxis-versichert.de" className="text-accent hover:underline">info@praxis-versichert.de</a><br/>
                  Telefon: <a href="tel:073168231" className="text-accent hover:underline">0731 / 68231</a>
                </p>
              </div>
              <Separator />
              <div>
                <p className="font-semibold text-foreground mb-2">An Deutsches Maklerforum AG:</p>
                <p className="text-muted-foreground">Datenschutzbeauftragter<br/>Deutsches Maklerforum AG<br/>Ziegetsdorfer Str. 118<br/>93051 Regensburg</p>
                <p className="text-muted-foreground pt-2">
                  E-Mail: <a href="mailto:datenschutzbeauftragter@deutsches-maklerforum.de" className="text-accent hover:underline">datenschutzbeauftragter@deutsches-maklerforum.de</a><br/>
                  Telefon: <a href="tel:09416022500" className="text-accent hover:underline">0941/6022-500</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </ResponsiveSection>

        {/* Stand der Datenschutzerklärung */}
        <ResponsiveSection spacing="sm" containerSize="md">
          <div className="text-center text-sm text-muted-foreground">
            <p>Stand: Januar 2025</p>
            <p className="mt-2">
              Zurück zum <Link to="/impressum" className="text-accent hover:underline">Impressum</Link>
            </p>
          </div>
        </ResponsiveSection>
      </main>

      <Footer />
      </div>
    </>
  );
};

export default Datenschutz;